// application/useCapacitacionDetalle.js  (detalle enriquecido; imágenes vía useImagenesDetalle). Sin JSX, sin Swal.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { capacitacionesRepository } from '../infrastructure/capacitaciones.repository';
import { catalogosRepository } from '../../shared/infrastructure/catalogos.repository';
import { mediaRepository } from '../../shared/infrastructure/media.repository';
import { useDescargarPdf } from '../../shared/application/useDescargarPdf';
import { useFotosProductores } from '../../shared/application/useFotosProductores';
import { archivoRepository } from '../../shared/infrastructure/archivo';
import { conDetalle } from '../domain/capacitacion.detalle';
import { useImagenesDetalle } from '../../shared/application/useImagenesDetalle';

const EMPTY = { entity: null, catalogs: {}, status: 'idle', error: null };

export const useCapacitacionDetalle = ({
  uuid,
  repository = capacitacionesRepository,
  catalogos = catalogosRepository,
  media = mediaRepository,
  archivo = archivoRepository,
} = {}) => {
  const [state, setState] = useState(EMPTY);
  const mounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!uuid) return;
    const id = ++requestId.current;
    setState(prev => ({ ...prev, status: 'loading', error: null }));
    try {
      const entity = await repository.get(uuid);
      const [actividades, municipios, localidades, productores] = await Promise.all([
        catalogos.actividades(),
        catalogos.municipios(),
        catalogos.localidades(entity.id_municipio),
        catalogos.productores(entity.id_ciclo),
      ]);
      if (mounted.current && id === requestId.current) {
        setState({ entity, catalogs: { actividades, municipios, localidades, productores }, status: 'success', error: null });
      }
    } catch (error) {
      if (mounted.current && id === requestId.current) setState({ ...EMPTY, status: 'error', error });
    }
  }, [uuid, repository, catalogos]);

  useEffect(() => {
    reload();
  }, [reload]);

  const data = useMemo(() => (state.entity ? conDetalle(state.entity, state.catalogs) : null), [state.entity, state.catalogs]);
  const uuidsAsistentes = useMemo(() => (data ? data.asistentes_detalle.map(p => p.uuid) : []), [data]);
  const { fotos } = useFotosProductores(uuidsAsistentes);
  const { descargar, descargando } = useDescargarPdf(repository.descargarPdf);
  /** PDF oficial de esta capacitación -> { blob, nombre } (null si ya hay una descarga en curso). Lanza DomainError. */
  const descargarPdf = useCallback(() => descargar(uuid), [descargar, uuid]);

  const setEntity = useCallback(transformar => {
    setState(prev => (prev.entity ? { ...prev, entity: transformar(prev.entity) } : prev));
  }, []);
  const imagenes = useImagenesDetalle({
    tipo: 'capacitacion',
    entity: state.entity,
    setEntity,
    media,
    archivo,
  });

  return {
    data,
    fotos,
    status: state.status,
    error: state.error,
    reload,
    descargarPdf,
    descargandoPdf: descargando,
    ...imagenes,
  };
};

export default useCapacitacionDetalle;
